/**
 * useActiveWorld.js
 * 
 * Drop-in Replacement Hook für die bisherige userId-Logik.
 * 
 * 🔒 SICHERHEIT:
 * - validateWorldId() MUSS vor kritischen Aktionen geprüft werden
 * - isSwitching blockt alle Aktionen während Team-Wechsel
 * - getAbortSignal() für Request-Cancellation bei World-Switch
 * 
 * MIGRATION:
 * 
 * VORHER:
 *   const [userId, setUserId] = useState(null);
 *   useEffect(() => {
 *     onAuthStateChanged(auth, (user) => {
 *       if (user) setUserId(user.uid);
 *     });
 *   }, []);
 * 
 * NACHHER:
 *   const { userId, canWrite, hasFeature, validateWorldId } = useActiveWorld();
 * 
 * Der Hook gibt die AKTIVE World-ID zurück:
 * - Für Owner: eigene User-ID
 * - Für Member: Team-Owner's User-ID
 */

import { useTeam } from '../contexts/TeamContext';

/**
 * Hook für Zugriff auf die aktive World
 * 
 * @returns {Object} World-Daten und Berechtigungen
 */
export const useActiveWorld = () => {
  const {
    currentUserId,
    activeWorldId,
    accountType,
    memberType,
    isOwner,
    isMember,
    isMasterMember,
    isStandardMember,
    
    // Multi-Team Support
    availableTeams,
    activeTeam,
    hasMultipleTeams,
    switchTeam,
    leaveTeam,
    
    // Team-Infos
    teamOwnerId,
    teamOwnerEmail,
    teamOwnerName,
    
    // Berechtigungen
    features,
    readOnly,
    
    // 🔒 SICHERHEIT
    isSwitching,
    switchVersion,
    validateWorldId,
    getAbortSignal,
    
    // Status
    isLoading,
    isInitialized,
    
    // Helper Functions
    hasFeature,
    canWrite,
    canAccessSettings,
    canManageTeam,
    reloadTeamData,
  } = useTeam();

  return {
    // === WICHTIG: userId ist jetzt die AKTIVE World ===
    // Das ist der Schlüssel für die Migration!
    userId: activeWorldId,
    
    // Original User-ID (der eingeloggte User)
    currentUserId,
    
    // Account-Typ
    accountType,
    memberType,           // 'standard' | 'master' (nur für members)
    isOwner,
    isMember,
    isMasterMember,       // Master Team Member (Vollzugriff)
    isStandardMember,     // Standard Team Member (nur Leads)
    
    // Multi-Team Support
    availableTeams,       // Alle verfügbaren Teams/Accounts
    activeTeam,           // Das aktive Team-Objekt mit Details
    hasMultipleTeams,     // Hat User Zugriff auf mehrere Accounts?
    switchTeam,           // Funktion zum Wechseln: switchTeam(teamId)
    leaveTeam,            // Funktion zum Self-Leave: leaveTeam(teamOwnerId)
    
    // Team-Infos (nur für Members relevant)
    teamOwnerId,
    teamOwnerEmail,
    teamOwnerName,
    
    // Berechtigungen
    features,
    readOnly,
    
    // 🔒 SICHERHEIT - KRITISCH für E-Mail-Versand etc.
    isSwitching,          // UI blocken wenn true!
    switchVersion,        // Ändert sich bei jedem Switch
    validateWorldId,      // validateWorldId(id) → true/false
    getAbortSignal,       // Signal für fetch() Abbruch bei Switch
    
    // Status
    isLoading,
    isInitialized,
    
    // Permission Checks
    hasFeature,        // hasFeature('leads') → true/false
    canWrite,          // canWrite() → true/false (nicht read-only)
    canAccessSettings, // canAccessSettings() → true/false
    canManageTeam,     // canManageTeam() → true/false (nur Owner)
    
    // Utility
    reloadTeamData,    // Daten neu laden nach Änderungen
  };
};

/**
 * Vereinfachter Hook - gibt nur userId zurück
 * Für einfache Fälle wo nur die World-ID gebraucht wird
 * 
 * @returns {string|null} Die aktive World-ID
 */
export const useWorldId = () => {
  const { activeWorldId } = useTeam();
  return activeWorldId;
};

/**
 * Hook für Feature-Checks
 * 
 * @param {string} feature - Das zu prüfende Feature ('leads', 'inbox', etc.)
 * @returns {boolean} Hat der User Zugriff auf dieses Feature?
 */
export const useHasFeature = (feature) => {
  const { hasFeature } = useTeam();
  return hasFeature(feature);
};

/**
 * Hook für Write-Permission
 * 
 * @returns {boolean} Kann der User schreiben (nicht read-only)?
 */
export const useCanWrite = () => {
  const { canWrite } = useTeam();
  return canWrite();
};

/**
 * 🔒 SICHERHEIT: Hook für sichere Aktionen
 * Gibt false zurück wenn gerade ein Switch läuft
 * 
 * @returns {boolean} Ist es sicher, Aktionen auszuführen?
 */
export const useIsSafeToAct = () => {
  const { isSwitching, isLoading, activeWorldId } = useTeam();
  return !isSwitching && !isLoading && !!activeWorldId;
};

export default useActiveWorld;
